/**
 * Le «i» dei numeri principali, coi conti fatti sui dati del periodo
 * (Davide 27/10/2026: «si deve capire in tutti i numeri come e' stato
 * calcolato quel risultato»). Un posto solo: pagina e test leggono da qui.
 */

import type { BiPostVendita, BiTotaleEconomia } from "./biApi";
import { calcolabili, eur, intero, marginePerc, n, perc } from "./biTesti";
import type { Spiegazione } from "./components/InfoTip";

/** «1 ordine», «2 ordini». */
export function ordini(quanti: number | string): string {
  return `${intero(quanti)} ${n(quanti) === 1 ? "ordine" : "ordini"}`;
}

function incertezza(e: BiTotaleEconomia): string | undefined {
  const parti: string[] = [];
  if (n(e.estimate_count) > 0)
    parti.push(
      `${ordini(e.estimate_count)} con almeno un costo stimato: il numero e' vicino al vero, non esatto.`,
    );
  if (n(e.not_computable_count) > 0)
    parti.push(
      `${ordini(e.not_computable_count)} senza margine perche' manca un costo: non entrano nel conto.`,
    );
  return parti.length ? parti.join(" ") : undefined;
}

export function spiegaFatturato(e: BiTotaleEconomia): Spiegazione {
  return {
    titolo: "Fatturato netto",
    testo:
      "Quanto hanno pagato i clienti, tolta l'IVA. Annullati, rimborsati, resi e ordini a 0 € non contano.",
    conto: [
      {
        etichetta: `Somma del netto di ${intero(n(e.orders) - n(e.net_missing_count))} ordini`,
        valore: eur(e.net_revenue),
      },
      { segno: "=", etichetta: "Fatturato netto", valore: eur(e.net_revenue) },
    ],
    incerto:
      n(e.net_missing_count) > 0
        ? `${ordini(e.net_missing_count)} fuori: non sappiamo il paese, quindi quanta IVA togliere.`
        : undefined,
  };
}

export function spiegaMargine(e: BiTotaleEconomia, postVendita?: number | null): Spiegazione {
  const costi = n(e.product_cost) + n(e.commission) + n(e.shipping) + n(e.packaging);
  const resi = postVendita == null ? null : n(postVendita);
  return {
    titolo: "Margine netto",
    testo:
      resi == null
        ? "Quello che resta a BS COM dopo prodotto, commissione del canale, spedizione, imballo e logistica AllService. Solo sugli ordini con tutti i costi."
        : "Quello che resta a BS COM dopo prodotto, commissione del canale, spedizione, imballo, logistica AllService e i resi e le sostituzioni del periodo. Solo sugli ordini con tutti i costi.",
    conto: [
      {
        etichetta: `Fatturato netto di ${intero(calcolabili(e))} ordini calcolabili`,
        valore: eur(e.net_revenue_computable),
      },
      { segno: "−", etichetta: "Costo prodotto (fornitore)", valore: eur(e.product_cost) },
      { segno: "−", etichetta: "Commissioni dei canali", valore: eur(e.commission) },
      {
        segno: "−",
        etichetta: "Spedizione (fattura Bracchi o BRT dove c'è, se no listino)",
        valore: eur(e.shipping),
      },
      {
        segno: "−",
        etichetta: "Imballo e logistica AllService (picking, etichetta)",
        valore: eur(e.packaging),
      },
      ...(resi == null
        ? []
        : [
            {
              segno: "−" as const,
              etichetta: "Resi e sostituzioni (pezzi, etichette, andata persa)",
              valore: eur(resi),
            },
          ]),
      {
        segno: "=" as const,
        etichetta: "Margine netto",
        valore: eur(n(e.net_revenue_computable) - costi - (resi ?? 0)),
      },
    ],
    incerto: incertezza(e),
  };
}

export function spiegaMarginePerc(e: BiTotaleEconomia): Spiegazione {
  return {
    titolo: "Margine %",
    testo:
      "Quanti euro restano ogni 100 € di fatturato netto. Si divide sugli stessi ordini calcolabili.",
    conto: [
      { etichetta: "Margine netto", valore: eur(e.margin) },
      {
        segno: "÷",
        etichetta: "Fatturato netto calcolabile",
        valore: eur(e.net_revenue_computable),
      },
      { segno: "×", etichetta: "100", valore: "" },
      { segno: "=", etichetta: "Margine %", valore: perc(marginePerc(e)) },
    ],
    incerto: incertezza(e),
  };
}

export function spiegaOrdini(e: BiTotaleEconomia, post: BiPostVendita | null): Spiegazione {
  return {
    titolo: "Ordini venduti",
    testo:
      "Ordini del periodo che sono vendite vere: senza annullati, rimborsati, resi e ordini a 0 €.",
    conto: [
      { etichetta: "Ordini venduti", valore: intero(e.orders) },
      ...(post
        ? [
            { etichetta: "fuori: annullati o rimborsati", valore: intero(post.cancelled_orders) },
            { etichetta: "fuori: con un reso", valore: intero(post.returned_orders) },
          ]
        : []),
    ],
  };
}

export function spiegaMedio(e: BiTotaleEconomia): Spiegazione {
  const venduti = n(e.orders) - n(e.net_missing_count);
  return {
    titolo: "Valore medio ordine",
    testo: "Quanto vale in media un ordine, senza IVA.",
    conto: [
      { etichetta: "Fatturato netto", valore: eur(e.net_revenue) },
      { segno: "÷", etichetta: "Ordini con netto", valore: intero(venduti) },
      {
        segno: "=",
        etichetta: "Valore medio",
        valore: eur(venduti > 0 ? n(e.net_revenue) / venduti : null, 2),
      },
    ],
    incerto:
      n(e.net_missing_count) > 0
        ? `${intero(e.net_missing_count)} ordini senza netto sono fuori dal conto.`
        : undefined,
  };
}

export function spiegaMargineMedio(e: BiTotaleEconomia): Spiegazione {
  const c = calcolabili(e);
  return {
    titolo: "Margine per ordine",
    testo: "Quanto guadagni in media su un ordine.",
    conto: [
      { etichetta: "Margine netto", valore: eur(e.margin) },
      { segno: "÷", etichetta: "Ordini calcolabili", valore: intero(c) },
      {
        segno: "=",
        etichetta: "Margine per ordine",
        valore: eur(c > 0 ? n(e.margin) / c : null, 2),
      },
    ],
    incerto: incertezza(e),
  };
}

export function spiegaPerdita(e: BiTotaleEconomia): Spiegazione {
  return {
    titolo: "Ordini in perdita",
    testo: "Ordini dove i costi superano il fatturato netto. La lista e' piu' in basso.",
    conto: [
      { etichetta: "Ordini con margine sotto zero", valore: intero(e.loss_count) },
      { etichetta: "Somma delle perdite", valore: eur(e.loss_amount) },
    ],
    incerto:
      n(e.estimate_count) > 0
        ? "Alcuni possono avere un costo stimato: aprili per controllare."
        : undefined,
  };
}

export function spiegaSostituzioni(e: BiTotaleEconomia): Spiegazione {
  return {
    titolo: "Sostituzioni",
    testo:
      "Pezzi rimandati al cliente (rotti o sbagliati). Il costo non e' dentro il margine: e' una spesa in piu'.",
    conto: [
      { etichetta: "Pezzi rimandati", valore: intero(e.replacements_count) },
      {
        segno: "+",
        etichetta: "Costo Itamoby/Ityhome dalla loro mail (gia' senza IVA)",
        valore: "",
      },
      { segno: "+", etichetta: "Costo AllService scritto a mano, diviso 1,22", valore: "" },
      { segno: "=", etichetta: "Costo senza IVA", valore: eur(e.replacements_cost_net) },
    ],
    incerto:
      n(e.replacements_count) > 0 && n(e.replacements_cost_net) === 0
        ? "Nessun costo registrato per queste sostituzioni: il totale e' 0 perche' manca il dato, non perche' erano gratis."
        : undefined,
  };
}

export function spiegaCosti(): Spiegazione {
  return {
    titolo: "Dove vanno 100 €",
    testo:
      "Ogni 100 € incassati senza IVA, quanti vanno al fornitore, al canale, al corriere e all'imballo, e quanti restano. Solo ordini calcolabili.",
  };
}
